import React, { useState, useEffect } from 'react';
import { Base_Url, AppointmentBooking } from '../../../config/config';
import { toast } from 'react-toastify';
import { useMyContext } from '../../../context/MyContext';
import {
  X,
  Calendar,
  Clock,
  Hash,
  Truck,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Lock,
  Sparkles,
  ArrowRight,
  Sun,
  Sunrise,
  Sunset,
  Moon,
  Edit3,
  RotateCcw,
  Check,
  CalendarCheck
} from 'lucide-react';
import { MdOutlineEventAvailable } from 'react-icons/md';

const PARTNER_OPTIONS = [
  { value: 'gati', label: 'Gati' },
  { value: 'delhivery', label: 'Delhivery' },
  { value: 'ecom_express', label: 'Ecom Express' },
  { value: 'ekart', label: 'Ekart' },
  { value: 'xpressbees', label: 'Xpressbees' },
  { value: 'bluedart', label: 'Bluedart' },
  { value: 'pickupxpress', label: 'Pickupxpress' },
];

const TIME_SLOTS = [
  {
    id: 'morning_1',
    title: 'Morning',
    time: '7 AM - 10 AM',
    badge: 'Early Morning',
    apiValue: '07:00 - 10:00 morning',
    icon: Sunrise
  },
  {
    id: 'morning_2',
    title: 'Morning',
    time: '10 AM - 2 PM',
    badge: 'Mid Morning',
    apiValue: '10:00 - 14:00 morning',
    icon: Sun
  },
  {
    id: 'evening',
    title: 'Evening',
    time: '2 PM - 6 PM',
    badge: 'Evening',
    apiValue: '14:00 - 18:00 evening',
    icon: Sunset
  },
  {
    id: 'night',
    title: 'Night',
    time: '6 PM - 9 PM',
    badge: 'Night',
    apiValue: '18:00 - 21:00 night',
    icon: Moon
  }
];

const extractDate = (dateVal) => {
  if (!dateVal) return '';
  if (typeof dateVal === 'string') {
    const iso = dateVal.match(/^\d{4}-\d{2}-\d{2}/);
    if (iso) return iso[0];
    const dmy = dateVal.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
    if (dmy) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${dmy[3]}-${pad(dmy[2])}-${pad(dmy[1])}`;
    }
  }
  try {
    const d = new Date(dateVal);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  } catch (e) {}
  return String(dateVal).slice(0, 10);
};

const extractPONumbers = (shipment) => {
  if (!shipment) return [''];
  if (Array.isArray(shipment.po_numbers) && shipment.po_numbers.length > 0) {
    return shipment.po_numbers.map(String).filter(Boolean);
  }
  if (Array.isArray(shipment.po_number) && shipment.po_number.length > 0) {
    return shipment.po_number.map(String).filter(Boolean);
  }
  if (typeof shipment.po_number === 'string' && shipment.po_number.trim()) {
    try {
      const parsed = JSON.parse(shipment.po_number);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed.map(String).filter(Boolean);
    } catch (e) {}
    if (shipment.po_number.includes(',')) {
      return shipment.po_number.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [shipment.po_number.trim()];
  }
  if (shipment.invoices) {
    try {
      const invs = typeof shipment.invoices === 'string' ? JSON.parse(shipment.invoices) : shipment.invoices;
      if (Array.isArray(invs) && invs.length > 0) {
        const poList = invs.map(i => i.invoice_no || i.po_number).filter(Boolean);
        if (poList.length > 0) return poList;
      }
    } catch (e) {}
  }
  return [''];
};

const mapSlotToId = (slotVal) => {
  if (!slotVal) return 'morning_1';
  const s = String(slotVal).toLowerCase();
  if (s === 'morning_1' || s.includes('07:00') || s.includes('7 am') || s.includes('early morning')) return 'morning_1';
  if (s === 'morning_2' || s.includes('10:00') || s.includes('10 am') || s.includes('mid morning')) return 'morning_2';
  if (s === 'evening' || s.includes('14:00') || s.includes('2 pm') || s.includes('evening')) return 'evening';
  if (s === 'night' || s.includes('18:00') || s.includes('6 pm') || s.includes('night')) return 'night';
  return 'morning_1';
};

export default function AppointmentModal({ isopen, onClose, orderId, shipment, onSuccess }) {
  const { isDarkMode } = useMyContext();
  const [formData, setFormData] = useState({
    orderId: orderId || '',
    appointmentDate: '',
    timeSlot: 'morning_1',
    poNumbers: [''],
    poExpiryDate: '',
    appointmentId: '',
    partner: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);

  const rawStatus = (shipment?.status || '').toLowerCase().trim();
  const isUndelivered = rawStatus.includes('undeliver') || rawStatus === 'ndr' || rawStatus.includes('failed') || rawStatus.includes('rto');
  const hasExistingAppointment = Boolean(shipment?.appointment_date || shipment?.appointment_id || shipment?.appointment_slot);

  // Sync state whenever modal opens or order/shipment changes
  useEffect(() => {
    if (isopen) {
      let detectedPartner = '';
      const rawPartner = (shipment?.courier_partner || shipment?.partner || shipment?.carrier_name || '').toLowerCase();
      const matched = PARTNER_OPTIONS.find(p => rawPartner.includes(p.value) || p.value.includes(rawPartner));
      if (matched) {
        detectedPartner = matched.value;
      } else if (rawPartner) {
        detectedPartner = rawPartner;
      }

      const initPO = extractPONumbers(shipment);
      const initDate = extractDate(shipment?.appointment_date || shipment?.start_date);
      const initSlot = mapSlotToId(shipment?.appointment_slot || shipment?.time_slot);
      const initExpiry = extractDate(shipment?.po_expiry_date || shipment?.end_date);
      const initApptId = shipment?.appointment_id || shipment?.id || '';

      setFormData({
        orderId: orderId || shipment?.order_id || '',
        appointmentDate: initDate,
        timeSlot: initSlot,
        poNumbers: initPO.length > 0 ? initPO : [''],
        poExpiryDate: initExpiry,
        appointmentId: initApptId,
        partner: detectedPartner || ''
      });

      // Default modes:
      // If undelivered -> enable reschedule mode directly
      // If already has appointment -> default to view mode with Edit button
      // If new -> edit mode active
      if (isUndelivered) {
        setIsRescheduling(true);
        setIsEditing(true);
      } else if (hasExistingAppointment) {
        setIsRescheduling(false);
        setIsEditing(false);
      } else {
        setIsRescheduling(false);
        setIsEditing(true);
      }

      setError('');
    }
  }, [isopen, orderId, shipment, isUndelivered, hasExistingAppointment]);

  if (!isopen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handlePOChange = (index, value) => {
    const newPONumbers = [...formData.poNumbers];
    newPONumbers[index] = value;
    setFormData(prev => ({
      ...prev,
      poNumbers: newPONumbers
    }));
    if (error) setError('');
  };

  const addPOField = () => {
    setFormData(prev => ({
      ...prev,
      poNumbers: [...prev.poNumbers, '']
    }));
  };

  const removePOField = (index) => {
    if (formData.poNumbers.length > 1) {
      const newPONumbers = formData.poNumbers.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        poNumbers: newPONumbers
      }));
    }
  };

  const getTimeSlotValue = (slot) => {
    const matched = TIME_SLOTS.find(s => s.id === slot);
    if (matched) return matched.apiValue;
    const timeSlotMap = {
      morning_1: '07:00 - 10:00 morning',
      morning_2: '10:00 - 14:00 morning',
      morning: '09:00 - 12:00 morning',
      evening: '14:00 - 18:00 evening',
      night: '18:00 - 21:00 night'
    };
    return timeSlotMap[slot] || slot;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const filteredPONumbers = formData.poNumbers.filter(po => po && po.trim() !== '');
    if (filteredPONumbers.length === 0) {
      setError('Please enter at least one valid PO Number');
      return;
    }

    if (!formData.appointmentDate) {
      setError('Please select an Appointment Date');
      return;
    }

    if (!formData.partner) {
      setError('Please select a Courier Partner');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cusId = localStorage.getItem('cus_id');
      const token = localStorage.getItem('token');

      const payload = {
        order_id: orderId || shipment?.order_id,
        start_date: formData.appointmentDate,
        end_date: formData.poExpiryDate || formData.appointmentDate,
        time_slot: getTimeSlotValue(formData.timeSlot),
        po_number: filteredPONumbers,
        appointment_id: formData.appointmentId || '',
        partner: formData.partner,
        ...(isRescheduling ? { reschedule: true, is_rescheduled: true, reason: 'Undelivered reattempt' } : {})
      };

      const response = await fetch(Base_Url + AppointmentBooking, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          cus_id: cusId,
          token: token
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok || data.status === 'success' || data.status === 'SUCCESS') {
        const successMsg = isRescheduling
          ? (data.message || 'Appointment rescheduled successfully!')
          : hasExistingAppointment
            ? (data.message || 'Appointment updated successfully!')
            : (data.message || 'Appointment registered successfully!');

        toast.success(successMsg, {
          duration: 3000,
          style: {
            borderRadius: '12px',
            background: '#10B981',
            color: '#fff',
            fontWeight: 600,
          }
        });
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
        onClose();
        setError('');
      } else {
        setError(data.message || (isRescheduling ? 'Failed to reschedule appointment' : 'Failed to register appointment'));
      }

    } catch (err) {
      setError('Network error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Helper info from shipment
  const waybill = shipment?.waybill || shipment?.lrn;
  let dropoffCity = '';
  try {
    const dropoff = shipment?.dropoff_location ? JSON.parse(shipment.dropoff_location || '{}') : {};
    dropoffCity = dropoff.city || dropoff.consignee_name || '';
  } catch (err) {}

  const today = new Date().toISOString().split('T')[0];

  const selectedSlotObj = TIME_SLOTS.find(s => s.id === formData.timeSlot) || TIME_SLOTS[0];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/65 backdrop-blur-md animate-in fade-in duration-200">
      
      {/* Modal Container */}
      <div className="relative w-full max-w-[620px] bg-white dark:bg-[#0e1222] border border-slate-200/90 dark:border-indigo-500/25 rounded-3xl shadow-2xl dark:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8),0_0_40px_rgba(99,102,241,0.2)] overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200">
        
        {/* ===== 1. MODERN HEADER ===== */}
        <div className={`relative px-5 sm:px-6 py-4 flex-shrink-0 border-b overflow-hidden transition-colors ${
          isRescheduling
            ? 'bg-gradient-to-r from-amber-600 via-rose-600 to-indigo-700 text-white border-amber-500/30'
            : isDarkMode
              ? 'bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border-indigo-500/20 text-white'
              : 'bg-gradient-to-r from-slate-50 via-indigo-50/70 to-purple-50/50 border-slate-200/90 text-slate-900'
        }`}>
          {/* Decorative glows */}
          <div className="absolute -top-16 -right-16 w-44 h-44 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-44 h-44 bg-purple-500/10 dark:bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md ring-2 text-white ${
                isRescheduling
                  ? 'bg-gradient-to-tr from-amber-500 to-rose-600 shadow-amber-500/30 ring-amber-300/40'
                  : 'bg-gradient-to-tr from-indigo-500 via-purple-600 to-violet-500 shadow-indigo-500/25 ring-indigo-200 dark:ring-white/20'
              }`}>
                {isRescheduling ? <RotateCcw size={20} className="animate-spin-slow" /> : <MdOutlineEventAvailable size={22} />}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-widest font-extrabold ${
                    isRescheduling
                      ? 'text-amber-200'
                      : 'text-indigo-600 dark:text-indigo-300'
                  }`}>
                    {isRescheduling ? 'Rescheduled Slot' : 'B2B Delivery Slot'}
                  </span>

                  {orderId && (
                    <span className="px-2 py-0.5 rounded-full text-[9.5px] font-mono font-bold bg-white/90 dark:bg-white/10 text-slate-700 dark:text-white/90 border border-slate-200/90 dark:border-white/10 shadow-2xs">
                      #{orderId}
                    </span>
                  )}

                  {isRescheduling ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9.5px] font-bold bg-white/25 text-white border border-white/30 backdrop-blur-xs">
                      <RotateCcw size={10} />
                      Reschedule Required
                    </span>
                  ) : hasExistingAppointment ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-semibold bg-emerald-50 dark:bg-emerald-400/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-400/30">
                      <CheckCircle2 size={10} className="text-emerald-500" />
                      Appointment Booked
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-semibold bg-indigo-50 dark:bg-indigo-400/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-400/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                      New Slot Booking
                    </span>
                  )}
                </div>

                <h2 className="text-base sm:text-lg font-black tracking-tight truncate mt-0.5">
                  {isRescheduling
                    ? 'Reschedule Appointment'
                    : isEditing && hasExistingAppointment
                      ? 'Edit Appointment Details'
                      : hasExistingAppointment
                        ? 'Appointment Details'
                        : 'Register Appointment'}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Edit Mode Toggle button when already booked & not in reschedule */}
              {hasExistingAppointment && !isRescheduling && (
                <button
                  type="button"
                  onClick={() => setIsEditing(prev => !prev)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    isEditing
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white ring-2 ring-indigo-400/30'
                      : 'bg-white dark:bg-white/15 hover:bg-indigo-50 dark:hover:bg-white/25 text-indigo-700 dark:text-white border border-indigo-200 dark:border-white/20'
                  }`}
                  title={isEditing ? "Cancel editing and view mode" : "Click to edit appointment details"}
                >
                  <Edit3 size={13} />
                  <span>{isEditing ? 'Editing Mode' : 'Edit'}</span>
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-8 h-8 rounded-full bg-white dark:bg-white/10 hover:bg-slate-100 dark:hover:bg-white/20 text-slate-500 dark:text-white/80 hover:text-slate-800 dark:hover:text-white border border-slate-200/90 dark:border-transparent flex items-center justify-center transition-all duration-200 hover:rotate-90 hover:scale-105 shadow-2xs shrink-0 cursor-pointer"
                title="Close (Esc)"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* ===== 2. SHIPMENT CONTEXT STRIP ===== */}
        <div className="px-5 sm:px-6 py-2.5 bg-slate-100/70 dark:bg-slate-900/60 border-b border-slate-200/80 dark:border-slate-800/80 flex items-center justify-between gap-3 text-xs flex-wrap">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300 font-medium">
            <span className="text-slate-400 dark:text-slate-400">AWB:</span>
            <span className="font-mono font-bold text-slate-800 dark:text-slate-100">
              {waybill || '—'}
            </span>
          </div>

          {dropoffCity && (
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 font-medium">
              <span className="text-slate-400 dark:text-slate-400">Destination:</span>
              <span className="font-semibold text-slate-800 dark:text-slate-100 truncate max-w-[150px]">
                {dropoffCity}
              </span>
            </div>
          )}

          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${isUndelivered ? 'bg-amber-500 animate-ping' : 'bg-emerald-500'}`} />
            <span className={`text-[11px] font-bold uppercase tracking-wider ${
              isUndelivered
                ? 'text-amber-600 dark:text-amber-400 font-extrabold'
                : 'text-emerald-600 dark:text-emerald-400'
            }`}>
              {shipment?.status || 'Manifested'}
            </span>
          </div>
        </div>

        {/* ===== 3. STATUS & ACTION BANNERS ===== */}
        {/* Undelivered / NDR Reschedule Callout */}
        {isUndelivered && (
          <div className="mx-5 sm:mx-6 mt-4 p-3.5 rounded-2xl bg-gradient-to-r from-amber-500/15 via-rose-500/10 to-amber-500/15 border border-amber-500/30 dark:border-amber-400/30 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                <AlertCircle size={18} />
              </div>
              <div>
                <p className="text-xs font-bold text-amber-800 dark:text-amber-200">
                  Undelivered / NDR Order
                </p>
                <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80">
                  Carrier reported delivery failure. Select a new delivery slot below to reattempt.
                </p>
              </div>
            </div>

            <div className="shrink-0 flex items-center gap-1.5">
              <span className="px-2.5 py-1 rounded-lg bg-amber-500/20 text-amber-700 dark:text-amber-300 font-bold text-[11px] border border-amber-500/30 flex items-center gap-1">
                <RotateCcw size={12} />
                Reschedule Active
              </span>
            </div>
          </div>
        )}

        {/* Existing Appointment Info Bar (when not undelivered & not editing) */}
        {!isUndelivered && hasExistingAppointment && !isEditing && (
          <div className="mx-5 sm:mx-6 mt-4 p-3.5 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-200/80 dark:border-indigo-500/30 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                <CalendarCheck size={18} />
              </div>
              <div>
                <p className="text-xs font-bold text-indigo-900 dark:text-indigo-200">
                  Appointment is Scheduled for {formData.appointmentDate || 'Confirmed Date'}
                </p>
                <p className="text-[11px] text-indigo-700/80 dark:text-indigo-300/80">
                  Slot: {selectedSlotObj.badge} ({selectedSlotObj.time}) • Partner: {formData.partner ? formData.partner.toUpperCase() : 'Assigned'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md shadow-indigo-600/20 active:scale-95 transition-all shrink-0 cursor-pointer flex items-center gap-1.5"
            >
              <Edit3 size={13} />
              <span>Edit Details</span>
            </button>
          </div>
        )}

        {/* ===== 4. FORM BODY ===== */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1">
          
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs font-semibold animate-in fade-in slide-in-from-top-1">
              <AlertCircle size={16} className="shrink-0 text-rose-500" />
              <span>{error}</span>
            </div>
          )}

          {/* Row 1: Order ID & Partner */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Order ID */}
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mb-1.5">
                <Lock size={12} className="text-slate-400" />
                Order ID
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.orderId || ''}
                  disabled
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-100/80 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-mono text-xs font-bold cursor-not-allowed select-all"
                />
              </div>
            </div>

            {/* Courier Partner */}
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mb-1.5">
                <Truck size={12} className="text-indigo-500" />
                Courier Partner <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="partner"
                  value={formData.partner}
                  onChange={handleInputChange}
                  disabled={!isEditing && !isRescheduling}
                  className={`w-full px-3.5 py-2.5 rounded-xl border text-xs font-semibold outline-none transition-all ${
                    !isEditing && !isRescheduling
                      ? 'border-slate-200 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 cursor-not-allowed'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 cursor-pointer'
                  }`}
                >
                  <option value="">Select Partner</option>
                  {PARTNER_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                  {/* Fallback if partner is custom like new carrier */}
                  {formData.partner && !PARTNER_OPTIONS.some(p => p.value === formData.partner) && (
                    <option value={formData.partner}>
                      {formData.partner.toUpperCase()}
                    </option>
                  )}
                </select>
              </div>
            </div>
          </div>

          {/* Row 2: PO Numbers */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Hash size={12} className="text-indigo-500" />
                Purchase Order (PO) Number(s) <span className="text-rose-500">*</span>
              </label>
              {(isEditing || isRescheduling) && (
                <button
                  type="button"
                  onClick={addPOField}
                  className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 hover:underline cursor-pointer transition-colors"
                >
                  <Plus size={13} />
                  Add PO Number
                </button>
              )}
            </div>

            <div className="space-y-2">
              {formData.poNumbers.map((po, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">
                      #
                    </span>
                    <input
                      type="text"
                      value={po}
                      onChange={(e) => handlePOChange(index, e.target.value)}
                      disabled={!isEditing && !isRescheduling}
                      placeholder={`Enter PO Number ${formData.poNumbers.length > 1 ? `(${index + 1})` : ''}`}
                      className={`w-full pl-8 pr-3.5 py-2.5 rounded-xl border text-xs font-medium outline-none transition-all ${
                        !isEditing && !isRescheduling
                          ? 'border-slate-200 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 cursor-not-allowed'
                          : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10'
                      }`}
                    />
                  </div>

                  {(isEditing || isRescheduling) && formData.poNumbers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePOField(index)}
                      className="w-9 h-9 rounded-xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 hover:bg-rose-100 dark:hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0 transition-all cursor-pointer"
                      title="Remove PO"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Row 3: Appointment Date & PO Expiry Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Appointment Date */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <Calendar size={12} className={isRescheduling ? 'text-amber-500' : 'text-indigo-500'} />
                  {isRescheduling ? 'New Reschedule Date' : 'Appointment Date'} <span className="text-rose-500">*</span>
                </label>
                {isRescheduling && (
                  <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                    Select New Date
                  </span>
                )}
              </div>
              <input
                type="date"
                name="appointmentDate"
                min={today}
                value={formData.appointmentDate}
                onChange={handleInputChange}
                disabled={!isEditing && !isRescheduling}
                className={`w-full px-3.5 py-2.5 rounded-xl border text-xs font-semibold outline-none transition-all ${
                  isRescheduling
                    ? 'border-amber-400 dark:border-amber-500 bg-amber-50/40 dark:bg-amber-950/20 text-slate-900 dark:text-white ring-2 ring-amber-400/20 cursor-pointer'
                    : !isEditing && !isRescheduling
                      ? 'border-slate-200 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 cursor-not-allowed'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 cursor-pointer'
                }`}
              />
            </div>

            {/* PO Expiry Date */}
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mb-1.5">
                <Calendar size={12} className="text-slate-400" />
                PO Expiry Date (Optional)
              </label>
              <input
                type="date"
                name="poExpiryDate"
                min={formData.appointmentDate || today}
                value={formData.poExpiryDate}
                onChange={handleInputChange}
                disabled={!isEditing && !isRescheduling}
                className={`w-full px-3.5 py-2.5 rounded-xl border text-xs font-semibold outline-none transition-all ${
                  !isEditing && !isRescheduling
                    ? 'border-slate-200 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 cursor-not-allowed'
                    : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 cursor-pointer'
                }`}
              />
            </div>
          </div>

          {/* Row 4: Select Time Slot (4 Interactive Slot Cards) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Clock size={12} className={isRescheduling ? 'text-amber-500' : 'text-indigo-500'} />
                {isRescheduling ? 'Reschedule Delivery Slot (4 Options)' : 'Select Delivery Slot (4 Options)'} <span className="text-rose-500">*</span>
              </label>
              {(isEditing || isRescheduling) && (
                <span className={`text-[10px] font-semibold ${isRescheduling ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400'}`}>
                  Tap to select
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {TIME_SLOTS.map((slot) => {
                const isSelected = formData.timeSlot === slot.id;
                const IconComponent = slot.icon;
                const isInteractive = isEditing || isRescheduling;

                return (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={!isInteractive}
                    onClick={() => {
                      if (isInteractive) {
                        setFormData(prev => ({ ...prev, timeSlot: slot.id }));
                        if (error) setError('');
                      }
                    }}
                    className={`relative p-2.5 rounded-2xl border text-left transition-all duration-200 flex flex-col justify-between gap-2 ${
                      !isInteractive
                        ? isSelected
                          ? 'border-indigo-400 bg-indigo-50/70 dark:bg-indigo-950/40 shadow-xs cursor-default'
                          : 'border-slate-200 dark:border-slate-800/60 bg-slate-50/50 dark:bg-slate-900/30 opacity-60 cursor-default'
                        : isSelected
                          ? isRescheduling
                            ? 'border-amber-500 bg-amber-50/90 dark:bg-amber-950/50 shadow-sm ring-2 ring-amber-500/25 cursor-pointer scale-[1.02]'
                            : 'border-indigo-500 bg-indigo-50/90 dark:bg-indigo-950/50 shadow-sm ring-2 ring-indigo-500/25 cursor-pointer scale-[1.02]'
                          : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50/50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                        isSelected
                          ? isRescheduling
                            ? 'bg-amber-600 text-white shadow-xs'
                            : 'bg-indigo-600 text-white shadow-xs'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}>
                        <IconComponent size={14} />
                      </div>
                      {isSelected && (
                        <CheckCircle2 size={14} className={isRescheduling ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400'} />
                      )}
                    </div>
                    <div>
                      <div className="text-[11px] font-bold text-slate-800 dark:text-slate-100 leading-tight">
                        {slot.badge}
                      </div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 font-mono mt-0.5">
                        {slot.time}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 5: Appointment ID (Optional) */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mb-1.5">
              <Sparkles size={12} className="text-slate-400" />
              Appointment ID <span className="text-[10px] font-normal lowercase text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              name="appointmentId"
              value={formData.appointmentId}
              onChange={handleInputChange}
              disabled={!isEditing && !isRescheduling}
              placeholder="e.g. APPT-981245 or Slot Token"
              className={`w-full px-3.5 py-2.5 rounded-xl border text-xs font-medium outline-none transition-all ${
                !isEditing && !isRescheduling
                  ? 'border-slate-200 dark:border-slate-800 bg-slate-100/70 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 cursor-not-allowed'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-800 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10'
              }`}
            />
          </div>
        </form>

        {/* ===== 5. MODAL FOOTER ===== */}
        <div className={`px-5 sm:px-6 py-3.5 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-xs transition-colors flex-shrink-0 ${
          isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-slate-50/90 border-slate-200'
        }`}>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 order-2 sm:order-1">
            <span className={`w-1.5 h-1.5 rounded-full ${isRescheduling ? 'bg-amber-500' : 'bg-indigo-500'}`} />
            <span>
              {isRescheduling
                ? 'Reschedule appointment reattempt with carrier warehouse'
                : 'Direct carrier warehouse slot integration'}
            </span>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end order-1 sm:order-2">
            {/* View mode toggle back button if editing existing */}
            {hasExistingAppointment && isEditing && !isRescheduling && (
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                disabled={loading}
                className="px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-all cursor-pointer"
              >
                Cancel Edit
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-all cursor-pointer disabled:opacity-50"
            >
              {hasExistingAppointment && !isEditing && !isRescheduling ? 'Close' : 'Cancel'}
            </button>

            {/* Action buttons depending on state */}
            {hasExistingAppointment && !isEditing && !isRescheduling ? (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="px-5 py-2 rounded-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:from-indigo-500 hover:to-purple-500 active:scale-[0.98] text-white transition-all duration-200 shadow-md shadow-indigo-600/30 hover:shadow-indigo-600/50 flex items-center justify-center gap-2 text-xs cursor-pointer"
              >
                <Edit3 size={14} />
                <span>Edit Appointment</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className={`px-5 py-2 rounded-xl font-bold active:scale-[0.98] text-white transition-all duration-200 shadow-md flex items-center justify-center gap-2 text-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                  isRescheduling
                    ? 'bg-gradient-to-r from-amber-500 via-rose-500 to-amber-600 hover:from-amber-600 hover:to-rose-600 shadow-amber-500/30'
                    : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:from-indigo-500 hover:to-purple-500 shadow-indigo-600/30 hover:shadow-indigo-600/50'
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>{isRescheduling ? 'Rescheduling...' : 'Saving...'}</span>
                  </>
                ) : isRescheduling ? (
                  <>
                    <RotateCcw size={14} />
                    <span>Confirm & Reschedule Appointment</span>
                    <ArrowRight size={14} />
                  </>
                ) : hasExistingAppointment ? (
                  <>
                    <Check size={14} />
                    <span>Update Appointment</span>
                    <ArrowRight size={14} />
                  </>
                ) : (
                  <>
                    <span>Register Appointment</span>
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}